/**
 * Template Editor Modal
 * Extracted from RevOpsPage.tsx for shared use between RevOps settings and Invoice Management.
 * Full template editor with Branding, Payment, and Content tabs plus live preview.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import PhoneInput from '@/components/ui/PhoneInput';
import {
  Palette,
  CreditCard as BankIcon,
  FileText,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  useRevOps,
  InvoiceTemplate,
  RevOpsInvoice,
  Organization,
} from '@/contexts/RevOpsContext';

// ============================================================================
// LOCAL INVOICE PREVIEW (for template preview only)
// ============================================================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function TemplateInvoicePreview({ invoice, template }: { invoice: RevOpsInvoice; template: InvoiceTemplate; organization?: Organization }) {
  const { fmt } = useFormattedDate();
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm" style={{ minWidth: '600px' }}>
      {/* Header with branding */}
      <div className="p-6 border-b border-gray-200" style={{ backgroundColor: template.primaryColor + '10' }}>
        <div className="flex justify-between items-start">
          <div>
            {template.showLogo && template.logoUrl ? (
              <img src={template.logoUrl} alt="Company Logo" className="h-12 mb-2" />
            ) : (
              <h2 className="text-2xl font-bold" style={{ color: template.primaryColor }}>
                {template.companyName}
              </h2>
            )}
            {template.companyAddress && (
              <p className="text-sm text-gray-600 whitespace-pre-line mt-1">{template.companyAddress}</p>
            )}
            {template.companyEmail && <p className="text-sm text-gray-600">{template.companyEmail}</p>}
            {template.companyPhone && <p className="text-sm text-gray-600">{template.companyPhone}</p>}
          </div>
          <div className="text-right">
            <h1 className="text-3xl font-bold" style={{ color: template.primaryColor }}>INVOICE</h1>
            <p className="text-lg font-semibold text-gray-800 mt-2">{invoice.invoiceNumber}</p>
            <div className="mt-2 text-sm text-gray-600">
              <p>Date: {fmt.medium(invoice.issuedDate)}</p>
              <p>Due: {fmt.medium(invoice.dueDate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
            <p className="font-semibold text-gray-900">{invoice.customer}</p>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
              invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
              invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {invoice.status.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="p-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 text-sm font-semibold text-gray-600">Description</th>
              <th className="text-center py-2 text-sm font-semibold text-gray-600">Qty</th>
              <th className="text-right py-2 text-sm font-semibold text-gray-600">Unit Price</th>
              <th className="text-right py-2 text-sm font-semibold text-gray-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, idx) => (
              <tr key={item.id || idx} className="border-b border-gray-100">
                <td className="py-3 text-sm text-gray-900">{item.description}</td>
                <td className="py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                <td className="py-3 text-sm text-gray-600 text-right">{formatCurrency(item.unitPrice)}</td>
                <td className="py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.tax && invoice.tax > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Tax</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.tax)}</span>
              </div>
            )}
            {invoice.discount && invoice.discount > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Discount</span>
                <span className="text-sm font-medium text-green-600">-{formatCurrency(invoice.discount)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 border-t border-gray-200 mt-2">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold" style={{ color: template.primaryColor }}>
                {formatCurrency(invoice.amount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      {template.defaultDueDays && (
        <div className="px-6 py-3 border-t border-gray-200">
          <div className="flex gap-2 text-sm">
            <span className="text-gray-500 font-medium">Payment Terms:</span>
            <span className="text-gray-900">Net {template.defaultDueDays} days</span>
          </div>
        </div>
      )}

      {/* Notes & Terms */}
      {(invoice.notes || template.paymentInstructions || template.termsAndConditions) && (
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          {invoice.notes && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Notes</h4>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}
          {template.showPaymentInstructions && template.paymentInstructions && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Payment Instructions</h4>
              <p className="text-sm text-gray-700">{template.paymentInstructions}</p>
            </div>
          )}
          {template.termsAndConditions && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Terms & Conditions</h4>
              <p className="text-sm text-gray-500">{template.termsAndConditions}</p>
            </div>
          )}
        </div>
      )}

      {/* Bank / Payment Details */}
      {template.showBankDetails && (template.bankName || (template.acceptedPaymentMethods && template.acceptedPaymentMethods.length > 0)) && (
        <div className="px-6 py-4 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment Details</h4>
          {template.bankName && (
            <div className="text-sm text-gray-700 space-y-0.5 mb-2">
              <p className="font-medium">{template.bankName}</p>
              {template.bankAccountName && <p>Account: {template.bankAccountName}</p>}
              {template.bankAccountNumber && <p>Account #: {template.bankAccountNumber}</p>}
              {template.bankRoutingNumber && <p>Routing #: {template.bankRoutingNumber}</p>}
              {template.bankSwiftCode && <p>SWIFT: {template.bankSwiftCode}</p>}
            </div>
          )}
          {template.acceptedPaymentMethods && template.acceptedPaymentMethods.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {template.acceptedPaymentMethods.map(m => (
                <span key={m} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                  {m.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
          {/* Per-method instructions */}
          {template.paymentMethodInstructions && template.acceptedPaymentMethods && template.acceptedPaymentMethods.some(m => template.paymentMethodInstructions?.[m]) && (
            <div className="mt-3 space-y-2">
              {template.acceptedPaymentMethods.filter(m => template.paymentMethodInstructions?.[m]).map(m => (
                <div key={m} className="text-sm">
                  <p className="font-medium text-gray-700 capitalize">{m.replace(/_/g, ' ')}</p>
                  <p className="text-gray-600 text-xs mt-0.5">{template.paymentMethodInstructions![m]}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment Portal Link */}
      {template.showPortalLink && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: template.primaryColor + '08', border: `1px solid ${template.primaryColor}30` }}>
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: template.primaryColor + '15' }}>
              <svg className="w-4 h-4" style={{ color: template.primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: template.primaryColor }}>Pay Online</p>
              <p className="text-xs text-gray-500 truncate mt-0.5">https://pay.example.com/invoice/...</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      {template.footerText && (
        <div className="p-4 text-center border-t border-gray-200">
          <p className="text-sm text-gray-500">{template.footerText}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TEMPLATE EDITOR MODAL
// ============================================================================

export interface TemplateEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: InvoiceTemplate | null;
  isNew?: boolean;
}

export function TemplateEditorModal({ isOpen, onClose, template, isNew }: TemplateEditorModalProps) {
  const { createTemplate, updateTemplate } = useRevOps();
  const [activeSection, setActiveSection] = useState<'branding' | 'payment' | 'content' | 'preview'>('branding');
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [companyName, setCompanyName] = useState(template?.companyName || '');
  const [companyAddress, setCompanyAddress] = useState(template?.companyAddress || '');
  const [companyPhone, setCompanyPhone] = useState(template?.companyPhone || '');
  const [companyEmail, setCompanyEmail] = useState(template?.companyEmail || '');
  const [companyWebsite, setCompanyWebsite] = useState(template?.companyWebsite || '');
  const [logoUrl, setLogoUrl] = useState(template?.logoUrl || '');
  const [primaryColor, setPrimaryColor] = useState(template?.primaryColor || '#2563eb');
  const [secondaryColor, setSecondaryColor] = useState(template?.secondaryColor || '#1e40af');
  const [accentColor, setAccentColor] = useState(template?.accentColor || '#3b82f6');

  // Banking
  const [bankName, setBankName] = useState(template?.bankName || '');
  const [bankAccountName, setBankAccountName] = useState(template?.bankAccountName || '');
  const [bankAccountNumber, setBankAccountNumber] = useState(template?.bankAccountNumber || '');
  const [bankRoutingNumber, setBankRoutingNumber] = useState(template?.bankRoutingNumber || '');
  const [bankSwiftCode, setBankSwiftCode] = useState(template?.bankSwiftCode || '');
  const [paypalEmail, setPaypalEmail] = useState(template?.paypalEmail || '');
  const [acceptedPaymentMethods, setAcceptedPaymentMethods] = useState<string[]>(
    template?.acceptedPaymentMethods || ['bank_transfer', 'credit_card', 'check']
  );
  const [paymentMethodInstructions, setPaymentMethodInstructions] = useState<Record<string, string>>(
    template?.paymentMethodInstructions || {}
  );

  // Content
  const [paymentInstructions, setPaymentInstructions] = useState(template?.paymentInstructions || '');
  const [termsAndConditions, setTermsAndConditions] = useState(template?.termsAndConditions || '');
  const [footerText, setFooterText] = useState(template?.footerText || '');
  const [defaultDueDays, setDefaultDueDays] = useState(template?.defaultDueDays || 30);
  const [invoicePrefix, setInvoicePrefix] = useState(template?.invoicePrefix || 'INV');
  const [taxId, setTaxId] = useState(template?.taxId || '');
  const [taxIdLabel, setTaxIdLabel] = useState(template?.taxIdLabel || 'Tax ID');

  // Display options
  const [showLogo, setShowLogo] = useState(template?.showLogo ?? true);
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(template?.showPaymentInstructions ?? true);
  const [showBankDetails, setShowBankDetails] = useState(template?.showBankDetails ?? false);
  const [showTaxId, setShowTaxId] = useState(template?.showTaxId ?? false);
  const [showPortalLink, setShowPortalLink] = useState(template?.showPortalLink ?? true);
  const logoFileRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo file must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoUrl(reader.result as string);
      setShowLogo(true);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setCompanyName(template.companyName);
      setCompanyAddress(template.companyAddress || '');
      setCompanyPhone(template.companyPhone || '');
      setCompanyEmail(template.companyEmail || '');
      setCompanyWebsite(template.companyWebsite || '');
      setLogoUrl(template.logoUrl || '');
      setPrimaryColor(template.primaryColor);
      setSecondaryColor(template.secondaryColor);
      setAccentColor(template.accentColor || '#3b82f6');
      setBankName(template.bankName || '');
      setBankAccountName(template.bankAccountName || '');
      setBankAccountNumber(template.bankAccountNumber || '');
      setBankRoutingNumber(template.bankRoutingNumber || '');
      setBankSwiftCode(template.bankSwiftCode || '');
      setPaypalEmail(template.paypalEmail || '');
      setAcceptedPaymentMethods(template.acceptedPaymentMethods || ['bank_transfer', 'credit_card', 'check']);
      setPaymentMethodInstructions(template.paymentMethodInstructions || {});
      setPaymentInstructions(template.paymentInstructions || '');
      setTermsAndConditions(template.termsAndConditions || '');
      setFooterText(template.footerText || '');
      setDefaultDueDays(template.defaultDueDays || 30);
      setInvoicePrefix(template.invoicePrefix || 'INV');
      setTaxId(template.taxId || '');
      setTaxIdLabel(template.taxIdLabel || 'Tax ID');
      setShowLogo(template.showLogo ?? true);
      setShowPaymentInstructions(template.showPaymentInstructions ?? true);
      setShowBankDetails(template.showBankDetails ?? false);
      setShowTaxId(template.showTaxId ?? false);
      setShowPortalLink(template.showPortalLink ?? true);
    }
  }, [template]);

  const handleSave = () => {
    setIsSaving(true);
    const templateData: Omit<InvoiceTemplate, 'id'> = {
      name,
      description,
      companyName,
      companyAddress,
      companyPhone,
      companyEmail,
      companyWebsite,
      logoUrl,
      primaryColor,
      secondaryColor,
      accentColor,
      bankName,
      bankAccountName,
      bankAccountNumber,
      bankRoutingNumber,
      bankSwiftCode,
      paypalEmail,
      acceptedPaymentMethods: acceptedPaymentMethods as InvoiceTemplate['acceptedPaymentMethods'],
      paymentMethodInstructions,
      paymentInstructions,
      termsAndConditions,
      footerText,
      defaultDueDays,
      invoicePrefix,
      taxId,
      taxIdLabel,
      showLogo,
      showPaymentInstructions,
      showBankDetails,
      showTaxId,
      showPortalLink,
      isDefault: template?.isDefault || false,
      updatedAt: new Date().toISOString(),
    };

    setTimeout(() => {
      if (isNew) {
        createTemplate(templateData);
      } else if (template) {
        updateTemplate(template.id, templateData);
      }
      setIsSaving(false);
      onClose();
    }, 500);
  };

  const togglePaymentMethod = (method: string) => {
    setAcceptedPaymentMethods(prev =>
      prev.includes(method)
        ? prev.filter(m => m !== method)
        : [...prev, method]
    );
  };

  // Preview template for display
  const previewTemplate: InvoiceTemplate = {
    id: 'preview',
    name,
    description,
    companyName,
    companyAddress,
    companyPhone,
    companyEmail,
    companyWebsite,
    logoUrl,
    primaryColor,
    secondaryColor,
    accentColor,
    bankName,
    bankAccountName,
    bankAccountNumber,
    bankRoutingNumber,
    bankSwiftCode,
    paypalEmail,
    acceptedPaymentMethods: acceptedPaymentMethods as InvoiceTemplate['acceptedPaymentMethods'],
    paymentMethodInstructions,
    paymentInstructions,
    termsAndConditions,
    footerText,
    defaultDueDays,
    invoicePrefix,
    showLogo,
    showPaymentInstructions,
    showBankDetails,
    showTaxId,
    showPortalLink,
    taxId,
    taxIdLabel,
    isDefault: false,
  };

  const previewInvoice: RevOpsInvoice = {
    id: 'preview',
    invoiceNumber: `${invoicePrefix}-001`,
    customer: 'Sample Customer Inc.',
    amount: 5000,
    subtotal: 4500,
    tax: 500,
    status: 'draft',
    dueDate: new Date(Date.now() + defaultDueDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    issuedDate: new Date().toISOString().split('T')[0],
    lineItems: [
      { id: '1', description: 'Consulting Services - Monthly', quantity: 1, unitPrice: 3000, amount: 3000 },
      { id: '2', description: 'Support Package', quantity: 1, unitPrice: 1500, amount: 1500 },
    ],
    notes: 'Thank you for your business!',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create Invoice Template' : 'Edit Invoice Template'}</DialogTitle>
          <DialogDescription>
            Configure branding, payment details, and content for your invoices
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Left side - Form */}
          <div className="w-1/2 overflow-y-auto pr-4">
            {/* Section tabs */}
            <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
              {[
                { id: 'branding', label: 'Branding', icon: <Palette className="h-4 w-4" /> },
                { id: 'payment', label: 'Payment', icon: <BankIcon className="h-4 w-4" /> },
                { id: 'content', label: 'Content', icon: <FileText className="h-4 w-4" /> },
              ].map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as typeof activeSection)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section.icon}
                  {section.label}
                </button>
              ))}
            </div>

            {activeSection === 'branding' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Standard Invoice"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
                  <textarea
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    rows={3}
                    placeholder={"123 Business St, Suite 100\nCity, State 12345"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <PhoneInput
                      value={companyPhone}
                      onChange={(v) => setCompanyPhone(v)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    placeholder="https://yourcompany.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                  <div className="space-y-2">
                    {logoUrl ? (
                      <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <img src={logoUrl} alt="Logo preview" className="h-10 max-w-[120px] object-contain" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 truncate">
                            {logoUrl.startsWith('data:') ? 'Uploaded image' : logoUrl}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setLogoUrl(''); if (logoFileRef.current) logoFileRef.current.value = ''; }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <input
                        ref={logoFileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-file-upload"
                      />
                      <button
                        type="button"
                        onClick={() => logoFileRef.current?.click()}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
                      >
                        <Upload className="w-4 h-4" />
                        {logoUrl ? 'Change Logo' : 'Upload Logo'}
                      </button>
                      <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                        <input
                          type="checkbox"
                          checked={showLogo}
                          onChange={(e) => setShowLogo(e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm">Show</span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-400">PNG, JPEG, or SVG. Max 2MB.</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Colors</label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Primary</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="h-10 w-14 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Secondary</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          className="h-10 w-14 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'payment' && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-800 mb-2">Bank Account Details</h4>
                  <p className="text-sm text-blue-600">
                    These details will appear on invoices when bank transfer is enabled
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="e.g., Chase Bank"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                    <input
                      type="text"
                      value={bankAccountName}
                      onChange={(e) => setBankAccountName(e.target.value)}
                      placeholder="Company Name LLC"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value)}
                      placeholder="****1234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
                    <input
                      type="text"
                      value={bankRoutingNumber}
                      onChange={(e) => setBankRoutingNumber(e.target.value)}
                      placeholder="021000021"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SWIFT/BIC Code (International)</label>
                  <input
                    type="text"
                    value={bankSwiftCode}
                    onChange={(e) => setBankSwiftCode(e.target.value)}
                    placeholder="CHASUS33"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    id="showBankDetails"
                    checked={showBankDetails}
                    onChange={(e) => setShowBankDetails(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="showBankDetails" className="text-sm text-gray-700">
                    Show bank details on invoice
                  </label>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Other Payment Methods</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Email</label>
                    <input
                      type="email"
                      value={paypalEmail}
                      onChange={(e) => setPaypalEmail(e.target.value)}
                      placeholder="payments@yourcompany.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Accepted Payment Methods</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'bank_transfer', label: 'Bank Transfer' },
                      { id: 'credit_card', label: 'Credit Card' },
                      { id: 'check', label: 'Check' },
                      { id: 'paypal', label: 'PayPal' },
                      { id: 'ach', label: 'ACH' },
                      { id: 'wire', label: 'Wire Transfer' },
                    ].map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => togglePaymentMethod(method.id)}
                        className={`px-3 py-2 rounded-lg text-sm border ${
                          acceptedPaymentMethods.includes(method.id)
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {method.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Per-Method Payment Instructions */}
                {acceptedPaymentMethods.length > 0 && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="font-medium text-gray-800 mb-1">Payment Instructions by Method</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Provide specific instructions for each payment method so customers know how to pay.
                    </p>
                    <div className="space-y-3">
                      {acceptedPaymentMethods.map(method => {
                        const labels: Record<string, { label: string; placeholder: string }> = {
                          bank_transfer: { label: 'Bank Transfer', placeholder: 'e.g., Wire funds to the bank account listed above. Include invoice number as reference.' },
                          credit_card: { label: 'Credit Card', placeholder: 'e.g., Call (555) 123-4567 to process credit card payment, or pay online via the portal link below.' },
                          check: { label: 'Check', placeholder: 'e.g., Make check payable to "Company Name LLC" and mail to: 123 Business St, Suite 100, City, ST 12345' },
                          paypal: { label: 'PayPal', placeholder: 'e.g., Send payment via PayPal to payments@yourcompany.com. Include invoice number in notes.' },
                          ach: { label: 'ACH', placeholder: 'e.g., Use the bank details above for ACH transfer. Allow 2-3 business days for processing.' },
                          wire: { label: 'Wire Transfer', placeholder: 'e.g., Use SWIFT code above for international wire transfers. Contact us for intermediary bank details.' },
                        };
                        const info = labels[method] || { label: method.replace(/_/g, ' '), placeholder: 'Provide instructions for this payment method' };
                        return (
                          <div key={method}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{info.label} Instructions</label>
                            <textarea
                              value={paymentMethodInstructions[method] || ''}
                              onChange={(e) => setPaymentMethodInstructions(prev => ({
                                ...prev,
                                [method]: e.target.value,
                              }))}
                              rows={2}
                              placeholder={info.placeholder}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Tax Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Label</label>
                      <input
                        type="text"
                        value={taxIdLabel}
                        onChange={(e) => setTaxIdLabel(e.target.value)}
                        placeholder="e.g., Tax ID, EIN, VAT"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Number</label>
                      <input
                        type="text"
                        value={taxId}
                        onChange={(e) => setTaxId(e.target.value)}
                        placeholder="XX-XXXXXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="showTaxId"
                      checked={showTaxId}
                      onChange={(e) => setShowTaxId(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="showTaxId" className="text-sm text-gray-700">
                      Show Tax ID on invoice
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'content' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
                    <input
                      type="text"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value)}
                      placeholder="INV"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Due Days</label>
                    <input
                      type="number"
                      value={defaultDueDays}
                      onChange={(e) => setDefaultDueDays(Number(e.target.value))}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Instructions</label>
                  <textarea
                    value={paymentInstructions}
                    onChange={(e) => setPaymentInstructions(e.target.value)}
                    rows={3}
                    placeholder="e.g., Payment is due within 30 days. Please include invoice number with payment."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="showPaymentInstructions"
                      checked={showPaymentInstructions}
                      onChange={(e) => setShowPaymentInstructions(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="showPaymentInstructions" className="text-sm text-gray-700">
                      Show payment instructions on invoice
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                  <textarea
                    value={termsAndConditions}
                    onChange={(e) => setTermsAndConditions(e.target.value)}
                    rows={4}
                    placeholder="e.g., All sales are final. Late payments may incur additional fees."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                  <input
                    type="text"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    placeholder="e.g., Thank you for your business!"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Payment Portal</h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="showPortalLink"
                      checked={showPortalLink}
                      onChange={(e) => setShowPortalLink(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="showPortalLink" className="text-sm text-gray-700">
                      Show payment portal link on invoice
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    When enabled, a "Pay Online" link will appear on the invoice for customers to view and pay online.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Preview */}
          <div className="w-1/2 border-l border-gray-200 pl-4 overflow-y-auto">
            <h4 className="text-sm font-medium text-gray-500 mb-3">PREVIEW</h4>
            <div className="transform scale-[0.65] origin-top-left" style={{ width: '154%' }}>
              <TemplateInvoicePreview
                invoice={previewInvoice}
                template={previewTemplate}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-gray-200 pt-4 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name || !companyName}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isNew ? 'Create Template' : 'Save Changes'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
